<template>
    <DashboardCard
        class="card"
        :icon="faEnvelopeOpen"
        title="Responses"
        :response-count="props.responseCount"
        action-label="Response details"
        :on-action-click="viewResponses"
        :show-error="showError"
        :on-reload-click="onReloadClick"
    >
        <template #content>
            <div v-if="loading" class="loading-container">
                <ResponsesSummaryLoading />
            </div>
            <div v-else class="container">
                <div
                    v-for="(summary, index) in props.summaries"
                    :key="index"
                    class="summary"
                >
                    <div
                        class="label-row"
                        data-testid="fm-dash-responses-label-row"
                    >
                        <span class="label">{{ summary.label }}s </span>
                        <span class="value">{{ summary.value || 0 }}% </span>
                        <span class="responses">
                            {{ formatResponseCount(summary.responses || 0) }}
                        </span>
                    </div>
                    <ProgressBar
                        :value="summary.value"
                        :show-value="false"
                        :bar-color="getBarColor(summary.label)"
                    />
                </div>
            </div>
        </template>
    </DashboardCard>
</template>

<script setup lang="ts">
import DashboardCard from '@/components/FrontlineManagerDash/DashboardCard.vue'
import ProgressBar from '@/components/PrimeVue/ProgressBar.vue'
import { TimeOptionLabel } from '@/utils/time'
import { faEnvelopeOpen } from 'fontawesome/pro-regular-svg-icons'
import ResponsesSummaryLoading from '@/components/FrontlineManagerDash/ResponsesSummaryLoading.vue'

interface ResponsesSummary {
    label: string
    value: number
    responses: number
}

interface Props {
    summaries: ResponsesSummary[]
    timeFilter: TimeOptionLabel
    loading: boolean
    responseCount: number
    showError?: boolean
    onReloadClick?: Function
}

const props = defineProps<Props>()

const viewResponses = () => {
    window.location.href = `/responses/${props.timeFilter.time_unit}/${props.timeFilter.time_value}`
}

const getBarColor = (label: string) => {
    switch (label) {
        case 'promoter':
            return '{success.400}'
        case 'passive':
            return '{gray.400}'
        case 'detractor':
        default:
            return '{error.300}'
    }
}

const formatResponseCount = (count: number) => {
    const formattedCount = count.toLocaleString()
    return `(${formattedCount} ${count === 1 ? 'response' : 'responses'})`
}
</script>

<style lang="less" scoped>
@import '~@/styles/prime-vue/variables.less';
@import '~@/styles/rain/variables.less';

.container,
.loading-container {
    display: flex;
    flex-direction: column;
    gap: @spacing-3xl;
    margin-top: 1rem;
}

.loading-container {
    height: 100%;
}

.summary {
    display: flex;
    flex-direction: column;
    gap: @spacing-xs;
}

.label-row {
    display: flex;
    font-weight: 500;
    font-size: @fontSize-md;
    line-height: @display-sm;

    .label {
        text-transform: capitalize;
        flex-grow: 2;
    }

    .responses {
        font-size: @fontSize-sm;
        margin-left: @spacing-sm;
    }
}
</style>
